import { z } from "zod";

/**
 * Brand Kit tokens — the machine-readable source of truth for an
 * organization's brand. The visual brand-kit document (jsonSpec) is a
 * deterministic projection of these tokens (see generateSpec.ts); tokens are
 * what the chat agent reads and writes, never the generated spec.
 *
 * Tokens are filled in section-by-section: the agent patches one section at a
 * time into a DRAFT (DraftBrandTokensSchema — every section optional). Only on
 * publish must the full BrandTokensSchema (required roles present) hold.
 */

export const BRAND_TOKENS_VERSION = 1;

const HexColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex color");

export const BrandColorRoleSchema = z.enum([
  "primary",
  "onPrimary",
  "secondary",
  "onSecondary",
  "accent",
  "background",
  "surface",
  "textPrimary",
  "textSecondary",
  "border",
  "danger",
  "success",
]);
export type BrandColorRole = z.infer<typeof BrandColorRoleSchema>;

export const BrandColorSchema = z.object({
  role: BrandColorRoleSchema,
  value: HexColorSchema,
  label: z.string().min(1).optional(),
});
export type BrandColor = z.infer<typeof BrandColorSchema>;

export const BrandFontRoleSchema = z.enum([
  "heading",
  "body",
  "mono",
  "button",
]);
export type BrandFontRole = z.infer<typeof BrandFontRoleSchema>;

export const BrandFontStackSchema = z.object({
  role: BrandFontRoleSchema,
  /** CSS font-family stack, e.g. "Georgia, 'Times New Roman', serif" */
  family: z.string().min(1),
  sizePx: z.number().int().min(8).max(96),
  weight: z.number().int().min(100).max(900),
  lineHeight: z.number().min(0.8).max(3).optional(),
  letterSpacing: z.string().optional(),
});
export type BrandFontStack = z.infer<typeof BrandFontStackSchema>;

// Logo shape is inferred (square vs wide) from the uploaded image — never
// asked of the user. icon/monochrome are intentionally out of scope for now.
export const BrandLogoRoleSchema = z.enum(["square", "wide"]);
export type BrandLogoRole = z.infer<typeof BrandLogoRoleSchema>;

export const BrandLogoVariantSchema = z
  .object({
    role: BrandLogoRoleSchema,
    /** Uploaded asset — rendered via the erp-doc:// scheme. */
    documentId: z.string().uuid().optional(),
    /** Externally hosted asset (CDN) — passed through as-is. */
    url: z.string().url().optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    alt: z.string().min(1),
  })
  .refine((logo) => Boolean(logo.documentId) !== Boolean(logo.url), {
    message: "Logo variant must have exactly one of documentId or url",
  });
export type BrandLogoVariant = z.infer<typeof BrandLogoVariantSchema>;

export const BrandButtonRecipeSchema = z.object({
  backgroundColor: HexColorSchema,
  color: HexColorSchema,
  borderRadiusPx: z.number().int().min(0).max(48),
  paddingY: z.number().int().min(0).max(48),
  paddingX: z.number().int().min(0).max(96),
  fontWeight: z.number().int().min(100).max(900),
});
export type BrandButtonRecipe = z.infer<typeof BrandButtonRecipeSchema>;

export const BrandDividerRecipeSchema = z.object({
  color: HexColorSchema,
  thicknessPx: z.number().int().min(1).max(8),
});
export type BrandDividerRecipe = z.infer<typeof BrandDividerRecipeSchema>;

export const BrandLinkRecipeSchema = z.object({
  color: HexColorSchema,
  underline: z.boolean(),
});
export type BrandLinkRecipe = z.infer<typeof BrandLinkRecipeSchema>;

export const BrandComponentRecipesSchema = z.object({
  button: BrandButtonRecipeSchema,
  divider: BrandDividerRecipeSchema,
  link: BrandLinkRecipeSchema,
});
export type BrandComponentRecipes = z.infer<typeof BrandComponentRecipesSchema>;

export const BrandVoiceRulesSchema = z.object({
  tone: z.string().min(1),
  doList: z.array(z.string().min(1)),
  dontList: z.array(z.string().min(1)),
  taglines: z.array(z.string().min(1)).optional(),
});
export type BrandVoiceRules = z.infer<typeof BrandVoiceRulesSchema>;

// --- Section arrays (uniqueness enforced; required-role checks live only in
// the strict publish schema below so drafts can be partial) ---

function uniqueRoles<T extends { role: string }>(
  label: string,
): (items: T[], ctx: z.RefinementCtx) => void {
  return (items, ctx) => {
    const roles = new Set(items.map((item) => item.role));
    if (roles.size !== items.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} roles must be unique`,
      });
    }
  };
}

export const BrandColorsSchema = z
  .array(BrandColorSchema)
  .superRefine(uniqueRoles<BrandColor>("Color"));
export const BrandTypographySchema = z
  .array(BrandFontStackSchema)
  .superRefine(uniqueRoles<BrandFontStack>("Font"));
export const BrandLogosSchema = z
  .array(BrandLogoVariantSchema)
  .superRefine(uniqueRoles<BrandLogoVariant>("Logo"));
export const BrandMemberBlockIdsSchema = z.array(z.string().uuid());

const REQUIRED_COLOR_ROLES: BrandColorRole[] = [
  "primary",
  "background",
  "textPrimary",
];
const REQUIRED_FONT_ROLES: BrandFontRole[] = ["heading", "body"];

/**
 * Draft tokens — every section is optional so the kit can be assembled
 * incrementally. Section payloads are still individually validated.
 */
export const DraftBrandTokensSchema = z.object({
  version: z.literal(BRAND_TOKENS_VERSION),
  colors: BrandColorsSchema.optional(),
  typography: BrandTypographySchema.optional(),
  logos: BrandLogosSchema.optional(),
  components: BrandComponentRecipesSchema.optional(),
  voice: BrandVoiceRulesSchema.optional(),
  memberBlockIds: BrandMemberBlockIdsSchema.optional(),
});
export type DraftBrandTokens = z.infer<typeof DraftBrandTokensSchema>;

/**
 * Strict, publishable tokens — every required section present. The generated
 * showcase still renders partial drafts; this is the gate for publishing.
 */
export const BrandTokensSchema = z.object({
  version: z.literal(BRAND_TOKENS_VERSION),
  colors: BrandColorsSchema.superRefine((colors, ctx) => {
    const roles = new Set(colors.map((color) => color.role));
    for (const required of REQUIRED_COLOR_ROLES) {
      if (!roles.has(required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Missing required color role "${required}"`,
        });
      }
    }
  }),
  typography: BrandTypographySchema.superRefine((fonts, ctx) => {
    const roles = new Set(fonts.map((font) => font.role));
    for (const required of REQUIRED_FONT_ROLES) {
      if (!roles.has(required)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Missing required font role "${required}"`,
        });
      }
    }
  }),
  logos: BrandLogosSchema,
  components: BrandComponentRecipesSchema,
  voice: BrandVoiceRulesSchema,
  /**
   * Template block ids for the org's branded starter blocks (header, footer,
   * etc.). The generated brand-kit document references each via BlockRef.
   */
  memberBlockIds: BrandMemberBlockIdsSchema.default([]),
});
export type BrandTokens = z.infer<typeof BrandTokensSchema>;

/** The patchable sections of a brand kit, used by the per-section update flow. */
export const BRAND_KIT_SECTIONS = [
  "colors",
  "typography",
  "logos",
  "components",
  "voice",
] as const;
export type BrandKitSection = (typeof BRAND_KIT_SECTIONS)[number];

/** Validates a single section's payload for the incremental draft flow. */
export const BrandKitSectionPayloadSchema = {
  colors: BrandColorsSchema,
  typography: BrandTypographySchema,
  logos: BrandLogosSchema,
  components: BrandComponentRecipesSchema,
  voice: BrandVoiceRulesSchema,
} satisfies Record<BrandKitSection, z.ZodTypeAny>;

export function getBrandColor(
  tokens: DraftBrandTokens,
  role: BrandColorRole,
): string | undefined {
  return tokens.colors?.find((color) => color.role === role)?.value;
}

export function getBrandFont(
  tokens: DraftBrandTokens,
  role: BrandFontRole,
): BrandFontStack | undefined {
  return tokens.typography?.find((font) => font.role === role);
}
